import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { LoadingService } from 'src/core/services/loading.service';
import { ModalService } from 'src/core/services/modal.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChartConfiguration, ChartDataset, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { WidgetService } from 'src/core/services/widget.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { filter, map, switchMap, tap } from 'rxjs';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';

@Component({
  selector: 'app-service-metrics',
  imports: [CommonModule, BaseChartDirective, TranslocoModule],
  templateUrl: './service-metrics.component.html',
  styleUrl: './service-metrics.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ServiceMetricsComponent {

  @Input() type: 'device' | 'site' = 'device';

  constructor(
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private widgetService: WidgetService,
    private daterangeService: DaterangeService,
    private modalService: ModalService,
    private selectedSiteService: SelectedSiteService
  ) { }

  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  servicemetrics: {};
  chartOptions!: ChartConfiguration<'line'>['options'];
  chartData: ChartConfiguration<'line'>['data'];
  selectedRange: number;

  public chartConfigs: { [key: string]: ChartConfiguration<'bar'> } = {};

  private baseDatasetStyle: Partial<ChartDataset<'bar'>> = {
    backgroundColor: '#DD6FFF'
  };


  ngOnInit(): void {

    //Device Calls
    if (this.type === 'device') {

      this.loadingService.$isServiceMetricsLoading.subscribe((isServiceMetricsLoading) => {
        this.isLoading = isServiceMetricsLoading;
        console.log("Changing Loading State to: ",isServiceMetricsLoading);
      });
      this.loadingService.$isServiceMetricsError.subscribe((isError) => {
        this.isError = isError;
      });
      this.loadingService.$isServiceMetricsEmpty.subscribe((isEmpty) => {
        this.isEmpty = isEmpty;
      });
      this.daterangeService.$selectedRangeServiceMetrics.subscribe((selectedRange) => {
        this.selectedRange = selectedRange;
      });
      this.selectedSystemService.$servicemetrics.pipe(
        tap(() => {
          // ✅ Reset before API call
          this.loadingService.$isServiceMetricsLoading.next(true);
          this.chartConfigs = {};
        }),
        switchMap(apiData =>

          this.daterangeService.$selectedRangeServiceMetrics.pipe(
            map((rangeValue) => {

              // Now apiData.map works
              return {
                apiData,
                rangeValue
              };
            })
          )
        )
      ).subscribe(
        {
          next: ({ apiData, rangeValue }) => {

            console.log("getting data", apiData);

            if (apiData) {
              //Assign values
              this.servicemetrics = apiData;
              this.selectedRange = rangeValue;

              //Create Charts based on Range
              this.chartConfigs['cdi'] = this.createChart(this.servicemetrics["cdi"] ? this.servicemetrics["cdi"]["time_windows"][rangeValue + ""] : null);
              this.chartConfigs['ci'] = this.createChart(this.servicemetrics["ci"] ? this.servicemetrics["ci"]["time_windows"][rangeValue + ""] : null);

              this.loadingService.$isServiceMetricsLoading.next(false);
            }
            else {
              this.servicemetrics = apiData;
              /* this.loadingService.$isServiceMetricsLoading.next(false); */
            }


          },
          error: (error) => {
            console.error('Error fetching service metrics:', error);
            this.loadingService.$isServiceMetricsError.next(true);
            this.loadingService.$isServiceMetricsLoading.next(false);
          }
        });

    }

    //Site Calls
    if (this.type === 'site') {

      this.loadingService.$isServiceMetricsLoadingSite.subscribe((isServiceMetricsLoading) => {
        this.isLoading = isServiceMetricsLoading;
      });
      this.loadingService.$isServiceMetricsError.subscribe((isError) => {
        this.isError = isError;
      });
      this.loadingService.$isServiceMetricsEmptySite.subscribe((isEmpty) => {
        this.isEmpty = isEmpty;
      });
      this.daterangeService.$selectedRangeServiceMetrics.subscribe((selectedRange) => {
        this.selectedRange = selectedRange;
      });
      this.selectedSiteService.$servicemetrics.pipe(
        tap(() => {
          // ✅ Reset before API call
          this.loadingService.$isServiceMetricsLoadingSite.next(true);
          this.chartConfigs = {};
        }),
        switchMap(apiData =>

          this.daterangeService.$selectedRangeServiceMetrics.pipe(
            map((rangeValue) => {

              // Now apiData.map works
              return {
                apiData,
                rangeValue
              };
            })
          )
        )
      ).subscribe(
        {
          next: ({ apiData, rangeValue }) => {

            if (apiData) {
              //Assign values
              this.servicemetrics = apiData;
              this.selectedRange = rangeValue;

              //Create Charts based on Range
              this.chartConfigs['cdi'] = this.createChart(this.servicemetrics["cdi"] ? this.servicemetrics["cdi"] ["time_windows"][rangeValue + ""] : null);
              this.chartConfigs['ci'] = this.createChart(this.servicemetrics["ci"] ? this.servicemetrics["ci"]["time_windows"][rangeValue + ""] : null);

              this.loadingService.$isServiceMetricsLoadingSite.next(false);
            }
            else {
              this.servicemetrics = apiData;
              this.loadingService.$isServiceMetricsLoadingSite.next(false);
            }


          },
          error: (error) => {
            console.error('Error fetching service metrics:', error);
            this.loadingService.$isServiceMetricsErrorSite.next(true);
          }
        });
        
    }

    }

  

  onMaximize(filter?: string) {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "service-metrics") {
      this.widgetService.closeWidget();
      return;
    }

    this.widgetService.expandWidget("service-metrics", filter);
  }

  checkWidgetIcon() {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "service-metrics") {
      return "minus";
    } else {
      return "plus";
    }
  }

  changeRange(range) {
    this.daterangeService.$selectedRangeServiceMetrics.next(range);
  }

  createChart(rowData: number[]): ChartConfiguration<'bar'> {
    return {
      type: 'bar',
      data: {
        labels: Array(rowData?.length).fill(''),
        datasets: [{
          ...this.baseDatasetStyle,
          data: rowData
        }]
      },
      options: {
        interaction: {
          mode: null
        },
        devicePixelRatio: 4,
        responsive: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        }
      }
    };
  }

  openHelp(section) {
    this.modalService.$helpModalChanged.next({ modal: section, open: true });
  }
}
